import {
  EMAIL_SEGMENT,
  EMAIL_SORT_FIELDS,
  EMAIL_STATUS_FILTER,
  SUBSCRIPTION_FILTER,
  SUBSCRIPTION_FILTER_LABEL
} from 'lib';
import React from 'react';
import {
  GroupLabel,
  Icons,
  Inset,
  MultiPill,
  PillRow,
  RangePill,
  SEGMENT_LABEL,
  SelectPill,
  T
} from './ui';

/**
 * Section 1's filter row — the eight audience filters of FR-029 and the count they produce.
 *
 * The count is the point. An operator narrowing an audience needs to see the effect immediately,
 * because a filter combination matching nobody looks identical to one not yet applied.
 *
 * It says "estimated" deliberately: a campaign's audience is resolved again at send time, so this
 * figure describes now, not the send. Saying so here is cheaper than explaining afterwards why the
 * numbers differ.
 *
 * All eight sit on one row of pills, as the design has them. Five take arrays and two take a date
 * range, none of which a plain select can express - so the pills summarise the selection and open
 * a checklist or a pair of date inputs. That keeps the design's row and the API's real capability,
 * which a row of single-select dropdowns would have quietly thrown away.
 */

const STATUS_OPTIONS = [
  { value: EMAIL_STATUS_FILTER.ANY, label: 'Verified & Unverified' },
  { value: EMAIL_STATUS_FILTER.VERIFIED, label: 'Verified only' },
  { value: EMAIL_STATUS_FILTER.UNVERIFIED, label: 'Unverified only' }
];

const SORT_OPTIONS = EMAIL_SORT_FIELDS.map((field) => ({
  value: field.value,
  label: field.label
}));

const DIRECTION_OPTIONS = [
  { value: 'desc', label: 'Recent / highest first' },
  { value: 'asc', label: 'Oldest / lowest first' }
];

const asOptions = (values) => values.map((value) => ({ value, label: value }));

/*
  Subscribed or not, answered from the fan's live subscriptions.

  A campaign saved before this change may still hold an old-platform tier name. It stays listed and
  selected, so reopening that campaign shows the filter it was saved with rather than dropping it.
*/
const subscriptionOptions = (selected = []) => [
  ...Object.values(SUBSCRIPTION_FILTER).map((value) => ({
    value,
    label: SUBSCRIPTION_FILTER_LABEL[value]
  })),
  ...selected
    .filter((value) => !SUBSCRIPTION_FILTER_LABEL[value])
    .map((value) => ({ value, label: `${value} (old tier)` }))
];

const AudienceFilters = ({
  filters,
  onChange,
  total,
  audienceTotal,
  loading,
  countries = [],
  teams = [],
  events = []
}) => {
  const setArray = (key) => (next) =>
    onChange({ ...filters, [key]: next.length ? next : undefined });

  const sort = filters.sort || { field: 'signupDate', direction: 'desc' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <GroupLabel>Audience filters</GroupLabel>

      <PillRow>
        <MultiPill
          label="Country"
          allLabel="All Countries"
          options={asOptions(countries)}
          selected={filters.country || []}
          onChange={setArray('country')}
        />
        <MultiPill
          label="Team Followed"
          allLabel="All Teams"
          options={asOptions(teams)}
          selected={filters.teams || []}
          onChange={setArray('teams')}
        />
        <MultiPill
          label="Segment"
          allLabel="All Segments"
          options={Object.values(EMAIL_SEGMENT).map((value) => ({
            value,
            label: SEGMENT_LABEL[value]
          }))}
          selected={filters.segments || []}
          onChange={setArray('segments')}
        />
        <MultiPill
          label="Subscription"
          allLabel="All Fans"
          options={subscriptionOptions(filters.subscriptionTiers)}
          selected={filters.subscriptionTiers || []}
          onChange={setArray('subscriptionTiers')}
        />
        <SelectPill
          label="Email Status"
          value={filters.emailStatus || EMAIL_STATUS_FILTER.ANY}
          options={STATUS_OPTIONS}
          onChange={(value) =>
            onChange({
              ...filters,
              emailStatus: value === EMAIL_STATUS_FILTER.ANY ? undefined : value
            })
          }
        />
        <RangePill
          label="Last Active"
          allLabel="Any time"
          range={filters.lastActive || {}}
          onChange={(range) => onChange({ ...filters, lastActive: range })}
        />
        <RangePill
          label="Signup Date"
          allLabel="All Time"
          range={filters.signupDate || {}}
          onChange={(range) => onChange({ ...filters, signupDate: range })}
        />
        <MultiPill
          label="Event Participation"
          allLabel="Any Event"
          options={events}
          selected={filters.eventParticipation || []}
          onChange={setArray('eventParticipation')}
        />
      </PillRow>

      {/*
        Sorting, on its own bar as the design has it.

        The table headers are clickable too and stay so, but a header you have to guess is
        clickable is not a discoverable control. Both drive the same state, so they cannot disagree.
      */}
      <Inset
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              color: T.body,
              fontSize: 13.5
            }}
          >
            <Icons.Sliders size={16} />
            Sorting by:
          </span>
          <SelectPill
            label="Sort"
            value={sort.field}
            options={SORT_OPTIONS}
            onChange={(field) => onChange({ ...filters, sort: { ...sort, field } })}
          />
          <SelectPill
            label="Order"
            value={sort.direction}
            options={DIRECTION_OPTIONS}
            onChange={(direction) => onChange({ ...filters, sort: { ...sort, direction } })}
          />
        </div>

        {/*
          Announced as well as shown. This number is the answer to "who will get this", and it
          changes as filters change — a screen-reader user adjusting a filter got no feedback at
          all before.
        */}
        <span role="status" aria-live="polite" style={{ fontSize: 13, color: T.muted }}>
          {loading
            ? 'Counting…'
            : `Showing ${(total ?? 0).toLocaleString()} out of ${(
                audienceTotal ?? 0
              ).toLocaleString()} users matching filters`}
        </span>
      </Inset>
    </div>
  );
};

export default AudienceFilters;
